/**
 * Company: SPHMedia
 * Description: The vertical latest article list item
 */

import { ReactElement } from "react";
import { RouteFactory } from "@app/routePaths";
import ImagePlaceholder from "@components/ArticleCard/components/ImagePlaceholder";
import type { ArticleCardProps } from "@components/ArticleCard/types";
import CreateTime from "@components/ArticleMeta/CreateTime";
import KeywordLink from "@components/ArticleMeta/KeywordLink";
import SectionLink from "@components/ArticleMeta/SectionLink";
import SubscriberFlag from "@components/ArticleMeta/SubscriberFlag/SubscriberFlag";
import Summary from "@components/ArticleMeta/Summary";
import TitleLink from "@components/ArticleMeta/TitleLink";
import UpdateTime from "@components/ArticleMeta/UpdateTime";
import ArticleThumbnail from "@components/ArticleThumbnail/ArticleThumbnail";
import { faCircle, faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  IS_PLAYWRIGHT,
  VERTICAL_COLOUR_VARIANTS,
  VerticalName,
} from "@util/constant";
import { cn, getDefaultImage, getFormattedRedirectionUrl } from "@util/helpers";
import cx from "classnames";

export interface VerticalLatestProps extends ArticleCardProps {
  sectionLinkRootClassName?: string;
  verticalName: VerticalName;
  kicker?: string;
}

/**
 * VerticalLatest
 * @description The vertical latest article list item
 * @param {VerticalLatestProps} props
 * @returns {ReactElement}
 */
export default function VerticalLatest({
  id = "",
  rootClassName = "",
  sectionLinkRootClassName = "font-poppins font-normal text-[12px] tracking-[1px] text-[--digital-red]",
  verticalName,
  articleData,
  kicker,
}: VerticalLatestProps): ReactElement {
  const {
    id: articleId = "",
    updated,
    edited,
    title,
    sections,
    paywall,
    tags,
    media,
    displaySetting,
    urlPath,
    blurb,
  } = articleData;
  const componentId = id || `article_${articleId}`;
  const firstMedia = media?.[0];
  const sectionUniqueName = sections?.[0]?.uniqueName ?? "";
  const thumbnail = firstMedia?.content
    ? firstMedia.content
    : {
        url: getDefaultImage(sectionUniqueName),
        alt: title,
      };
  const isVideo =
    firstMedia?.content?.type === "brightcoveVideo" ||
    firstMedia?.content?.type === "youtubeVideo";
  const articleLink = urlPath
    ? getFormattedRedirectionUrl(urlPath)
    : RouteFactory.article(id);

  return (
    <div
      id={componentId}
      data-testid={componentId}
      className={cx("flex flex-row gap-x-4", rootClassName)}
    >
      <div className="flex w-full-120px flex-col">
        {sections && sections.length > 0 ? (
          <SectionLink
            rootClassName={cx(
              sectionLinkRootClassName,
              VERTICAL_COLOUR_VARIANTS[verticalName].textStyle,
              VERTICAL_COLOUR_VARIANTS[verticalName].linkStyle
            )}
            sectionName={sections?.[0]?.name}
            sectionUniqueName={sectionUniqueName}
          />
        ) : null}

        {kicker ? (
          <div
            className="break-words font-poppins text-4xs font-light text-gray-515"
            data-testid="vertical-latest-kicker-component"
          >
            {kicker}
          </div>
        ) : null}

        {paywall && paywall.contentAccess === "1" ? (
          <SubscriberFlag className="font-poppins text-4xs text-gray-850" />
        ) : null}

        <TitleLink
          rootClassName={cx(
            "font-poppins font-semibold text-xs !leading-normal text-gray-850",
            VERTICAL_COLOUR_VARIANTS[verticalName].hoverStyle
          )}
          articleId={articleId}
          title={displaySetting?.displayHeadline || title}
          slug={urlPath}
        />

        {blurb ? (
          <Summary
            rootClassName="font-poppins text-xs font-normal !leading-normal text-gray-850"
            summary={blurb}
          />
        ) : null}
        <div
          className={cx(
            "mt-2 items-center gap-x-4 gap-y-1",
            "lg:flex lg:flex-row lg:flex-wrap"
          )}
        >
          {updated ? (
            <div className="flex flex-row gap-x-2">
              <CreateTime created={updated} />
              {displaySetting?.hasUpdatedTimeDisplayed ? (
                <UpdateTime
                  updated={updated}
                  edited={edited}
                  rootClassName="text-red"
                />
              ) : null}
            </div>
          ) : null}
          {tags ? (
            <div
              className={cn(
                "mt-2 flex gap-2 text-3xs text-gray-675",
                "lg:mt-0"
              )}
            >
              {tags.slice(0, 2).map((tag) => {
                return (
                  <KeywordLink
                    key={tag.name}
                    tagName={tag.name}
                    tagUrlPath={tag.urlPath}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className="relative flex max-h-[80px]">
        {!IS_PLAYWRIGHT ? (
          <>
            <ArticleThumbnail
              rootClassName="w-120px"
              thumbnail={thumbnail}
              orientation="landscape"
              imgRootClassName="aspect-ratio-3x2 w-full"
              articleLink={articleLink}
            />

            {isVideo ? (
              <div className="fa-stack absolute bottom-6 left-10">
                <FontAwesomeIcon
                  icon={faCircle}
                  inverse
                  className="fa-stack-2x"
                />
                <FontAwesomeIcon icon={faPlay} className="fa-stack-1x" />
              </div>
            ) : null}
          </>
        ) : (
          <ImagePlaceholder aspectRatio={"aspect-3x2"} className="w-[120px]" />
        )}
      </div>
    </div>
  );
}
